import { useState } from 'react';
import {
  Stack,
  Text,
  Textarea,
  Button,
  Paper,
  Group,
  Loader,
  Center,
  Badge,
  Timeline,
  ThemeIcon,
} from '@mantine/core';
import { IconSend, IconNote, IconUser } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAssetNotes, useAddAssetNote } from '@/hooks/api/useAuditLogs';
import { formatDate } from '@/lib/utils';

interface AssetNotesProps {
  organizationId: string;
  assetId: string;
}

export function AssetNotes({ organizationId, assetId }: AssetNotesProps) {
  const [newNote, setNewNote] = useState('');
  const { data: notes, isLoading } = useAssetNotes(organizationId, assetId);
  const addNote = useAddAssetNote();

  const handleSubmit = async () => {
    if (!newNote.trim()) return;

    try {
      await addNote.mutateAsync({
        organizationId,
        assetId,
        note: newNote.trim(),
      });
      setNewNote('');
      notifications.show({
        title: 'Note added',
        message: 'Your note has been added to the asset history',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to add note. Please try again.',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="md">
      {/* Add new note */}
      <Paper withBorder p="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            Add a Note
          </Text>
          <Textarea
            placeholder="Type your note here..."
            value={newNote}
            onChange={(e) => setNewNote(e.currentTarget.value)}
            minRows={2}
            maxRows={6}
            autosize
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleSubmit}
              loading={addNote.isPending}
              disabled={!newNote.trim()}
              size="sm"
            >
              Add Note
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* Notes history */}
      <div>
        <Text size="sm" fw={500} mb="sm">
          Notes History
        </Text>

        {isLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : notes && notes.length > 0 ? (
          <Timeline active={-1} bulletSize={24} lineWidth={2}>
            {notes.map((note) => (
              <Timeline.Item
                key={note.id}
                bullet={
                  <ThemeIcon size={24} radius="xl" variant="light" color="blue">
                    <IconNote size={14} />
                  </ThemeIcon>
                }
                title={
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      Note
                    </Text>
                    <Badge size="xs" variant="light" color="gray">
                      {formatDate(note.created_at)}
                    </Badge>
                  </Group>
                }
              >
                <Text size="sm" mt={4}>
                  {note.change_summary}
                </Text>
                {note.user_id && (
                  <Group gap={4} mt={4}>
                    <IconUser size={12} color="gray" />
                    <Text size="xs" c="dimmed">
                      {note.user_id.slice(0, 8)}...
                    </Text>
                  </Group>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <Paper withBorder p="xl">
            <Text ta="center" c="dimmed" size="sm">
              No notes yet. Add the first note above.
            </Text>
          </Paper>
        )}
      </div>
    </Stack>
  );
}
